import { useState, useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../utils/Auth';
import { useWindowSize } from '../../../utils/hooks/useWindowSize';
import { motion, AnimatePresence } from 'motion/react';
import Widget from './Widget';
import styles from './WidgetCollection.module.css';

export default function WidgetCollection({ qrIdFilter = null }) {

    const { widgets, liquidQRs } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { width } = useWindowSize();

    // Extract filter from location state or use prop (prop takes precedence)
    const { filterQrId } = location.state || {};
    const [activeFilters, setActiveFilters] = useState({
        qrId: qrIdFilter || filterQrId || null
    });

    // Sync activeFilters when qrIdFilter prop changes (for embedded mode)
    useEffect(() => {
        if (qrIdFilter !== null) {
            setActiveFilters(prev => ({
                ...prev,
                qrId: qrIdFilter
            }));
        }
    }, [qrIdFilter]);
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);

    const handleNavigate = () => navigate('/widgets', { state: { generator: true }});

    const handleQrFilterSelect = (qrId) => {
        setActiveFilters(prev => ({
            ...prev,
            qrId: qrId
        }));
        setShowFilterDropdown(false);
    };

    // Filter widgets based on active filters
    const filteredWidgets = useMemo(() => {
        if (!widgets) return [];
        
        let filtered = [...widgets];
        
        // Filter by QR ID
        if (activeFilters.qrId) {
            filtered = filtered.filter(w => w.qr_id === activeFilters.qrId);
        }
        
        // Additional filters can be added here
        // Example: if (activeFilters.displayOn) { ... }
        
        return filtered;
    }, [widgets, activeFilters]);

    const clearFilter = (filterKey) => {
        setActiveFilters(prev => ({
            ...prev,
            [filterKey]: null
        }));
    };

    const clearAllFilters = () => {
        setActiveFilters({
            qrId: null
        });
    };

    const hasActiveFilters = useMemo(() => {
        return Object.values(activeFilters).some(val => val !== null);
    }, [activeFilters]);

    // Filter QRs to only show those that have widgets
    const qrsWithWidgets = useMemo(() => {
        if (!liquidQRs || !widgets) return [];
        return liquidQRs.filter(qr => 
            widgets.some(widget => widget.qr_id === qr.id)
        );
    }, [liquidQRs, widgets]);

    const iconSize = useMemo(() => {
        if (width > 768) {
            return '15rem';
        } else if (width > 500) {
            return '12rem';
        };
    }, [width]);

    const variants = {
        initial: { opacity: 0, x: '6rem' },
        animate: { opacity: 1, x: 0 }
    };

    // If qrIdFilter is provided, we're in embedded mode (QrInformation page)
    const isEmbedded = !!qrIdFilter;

    if (isEmbedded) {
        return (
            <div className={styles.embeddedWrapper}>
                <div className={styles.embeddedCollection}>
                    {filteredWidgets?.length > 0 ? (
                        <div className={styles.widgets}>
                            {filteredWidgets.map((w) => (
                                <Widget widget={w} key={w.id} />
                            ))}
                        </div>
                    ) : (
                        <div className={styles.noWidgets}>
                            <p>No widgets created for this QR yet</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <div className={styles.left}>
                    <h5 className={styles.header}>What is a widget?</h5>
                    <div className={styles.description}>
                        <p>Instantly bridge the gap between your desktop site and mobile experiences.</p>
                        <p>
                            Use our<button onClick={handleNavigate} className={styles.generatorLink}>Widget Generator</button>to create and deploy dynamic, scannable
                            QR codes directly on any webpage. Your custom widget is a sleek side-drawer component that users simply click to 
                            reveal and scan your dedicated QR code for immediate mobile access to apps, links, or specific content.
                        </p>
                        <p><strong>Harness the power of LiquidQR to drive mobile engagement and simplify user handoffs.</strong></p>
                    </div>
                </div>
                <div className={styles.right}>
                    <AnimatePresence>
                        <motion.i 
                            className={`fa-duotone fa-solid fa-left-to-bracket ${styles.drawerIcon}`} 
                            style={{'--fa-primary-color': '#161d2d', '--fa-secondary-color': '#607ca7', '--fa-secondary-opacity': '1', fontSize: iconSize}}
                            transition={{duration: .25}}
                            variants={variants}
                            initial='initial'
                            animate='animate'
                        />
                    </AnimatePresence>
                </div>
            </div>
            <div className={styles.collection}>
                <div className={styles.collectionHeader}>
                    <div className={styles.headerTop}>
                        <h5 className={styles.header}>Collection</h5>
                        <div className={styles.filterControls}>
                            <div className={styles.filterDropdownWrapper}>
                                <button 
                                    className={styles.filterButton}
                                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                >
                                    Filter by QR
                                    <i className={`far fa-chevron-down ${styles.chevron} ${showFilterDropdown ? styles.open : ''}`} />
                                </button>
                                {showFilterDropdown && (
                                    <>
                                        <div className={styles.dropdownBackdrop} onClick={() => setShowFilterDropdown(false)} />
                                        <div className={styles.filterDropdown}>
                                            <div className={styles.dropdownHeader}>
                                                <span>Select QR Code</span>
                                                {activeFilters.qrId && (
                                                    <button 
                                                        className={styles.dropdownClear}
                                                        onClick={() => {
                                                            clearFilter('qrId');
                                                            setShowFilterDropdown(false);
                                                        }}
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>
                                            <div className={styles.dropdownList}>
                                                {qrsWithWidgets?.map(qr => (
                                                    <button
                                                        key={qr.id}
                                                        className={`${styles.dropdownItem} ${activeFilters.qrId === qr.id ? styles.selected : ''}`}
                                                        onClick={() => handleQrFilterSelect(qr.id)}
                                                    >
                                                        <div className={styles.qrInfo}>
                                                            <span className={styles.qrNickname}>{qr.data?.nickname || 'Untitled'}</span>
                                                            <span className={styles.qrId}>{qr.id}</span>
                                                        </div>
                                                        {activeFilters.qrId === qr.id && (
                                                            <i className="far fa-check" />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    {hasActiveFilters && (
                        <div className={styles.filterBar}>
                            <div className={styles.filterTags}>
                                {activeFilters.qrId && (
                                    <div className={styles.filterTag}>
                                        <span>QR: {liquidQRs?.find(qr => qr.id === activeFilters.qrId)?.data?.nickname || activeFilters.qrId}</span>
                                        <button onClick={() => clearFilter('qrId')} className={styles.filterClose}>
                                            <i className="far fa-times" />
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button onClick={clearAllFilters} className={styles.clearAll}>
                                Clear all filters
                            </button>
                        </div>
                    )}
                </div>
                {filteredWidgets?.length > 0 ? (
                    <div className={styles.widgets}>
                        {filteredWidgets.map((w,i) => (
                            <Widget widget={w} key={w.id} />
                        ))}
                    </div>
                ) : (
                    <div className={styles.noWidgets}>
                        {hasActiveFilters ? (
                            <p>No widgets match the current filters.</p>
                        ) : (
                            <>
                                <p>Widgets you create will appear here.</p>
                                <button className={styles.btn} onClick={handleNavigate}>Start creating!</button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};